import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export type WriteOffStatus = 'applied' | 'voided';

export interface WriteOff {
  id: string;
  tenantId: string;
  writeOffNumber: string;
  invoiceId: string;
  clientId: string;
  clientName: string;
  createdByName: string;
  practiceEntityId: string | null;
  amount: number;
  reason: string;
  serviceTypeCode: string | null;
  jobId: string | null;
  transactionDate: string;
  status: WriteOffStatus;
  voidedAt: string | null;
  voidedReason: string | null;
  createdById: string;
  createdAt: string;
  updatedAt: string;
}

export interface WriteOffListParams {
  clientId?: string;
  invoiceId?: string;
  status?: WriteOffStatus;
  practiceEntityId?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  pageSize?: number;
}

export interface CreateWriteOffPayload {
  invoice_id: string;
  amount: number;
  reason: string;
  service_type_code?: string | null;
  job_id?: string | null;
  practice_entity_id?: string | null;
  transaction_date?: string;
}

export interface VoidWriteOffPayload {
  reason: string;
}

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const writeOffsKeys = {
  all: ['billing', 'write-offs'] as const,
  lists: () => [...writeOffsKeys.all, 'list'] as const,
  list: (params?: WriteOffListParams) => [...writeOffsKeys.lists(), params] as const,
  detail: (id: string) => [...writeOffsKeys.all, 'detail', id] as const,
};

// ─── Mapper ─────────────────────────────────────────────────────────────────

function mapWriteOff(raw: Record<string, unknown>): WriteOff {
  return {
    id: raw.id as string,
    tenantId: (raw.tenant_id || raw.tenantId || '') as string,
    writeOffNumber: (raw.write_off_number || raw.writeOffNumber || '') as string,
    invoiceId: (raw.invoice_id || raw.invoiceId || '') as string,
    clientId: (raw.client_id || raw.clientId || '') as string,
    clientName: (raw.client_name || raw.clientName || '') as string,
    createdByName: (raw.created_by_name || raw.createdByName || '') as string,
    practiceEntityId: (raw.practice_entity_id ?? raw.practiceEntityId ?? null) as string | null,
    amount: Number(raw.amount || 0),
    reason: (raw.reason || '') as string,
    serviceTypeCode: (raw.service_type_code ?? raw.serviceTypeCode ?? null) as string | null,
    jobId: (raw.job_id ?? raw.jobId ?? null) as string | null,
    transactionDate: (raw.transaction_date || raw.transactionDate || '') as string,
    status: (raw.status || 'applied') as WriteOffStatus,
    voidedAt: (raw.voided_at ?? raw.voidedAt ?? null) as string | null,
    voidedReason: (raw.voided_reason ?? raw.voidedReason ?? null) as string | null,
    createdById: (raw.created_by_id || raw.createdById || '') as string,
    createdAt: (raw.created_at || raw.createdAt || '') as string,
    updatedAt: (raw.updated_at || raw.updatedAt || '') as string,
  };
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * Fetch paginated list of write-offs.
 */
export function useWriteOffs(params: WriteOffListParams = {}) {
  return useQuery({
    queryKey: writeOffsKeys.list(params),
    queryFn: async () => {
      const queryParams: Record<string, string | number> = {};
      if (params.clientId) queryParams.client_id = params.clientId;
      if (params.invoiceId) queryParams.invoice_id = params.invoiceId;
      if (params.status) queryParams.status = params.status;
      if (params.practiceEntityId) queryParams.practice_entity_id = params.practiceEntityId;
      if (params.dateFrom) queryParams.date_from = params.dateFrom;
      if (params.dateTo) queryParams.date_to = params.dateTo;
      if (params.page) queryParams.page = params.page;
      if (params.pageSize) queryParams.pageSize = params.pageSize;

      const response = await apiClient.get<
        ApiResponse<{ writeOffs: Record<string, unknown>[]; total: number }>
      >('/billing/write-offs', { params: queryParams });

      const data = response.data.data;
      return {
        writeOffs: (data.writeOffs || []).map(mapWriteOff),
        total: data.total || 0,
      };
    },
  });
}

/**
 * Fetch a single write-off by ID.
 */
export function useWriteOff(id: string) {
  return useQuery({
    queryKey: writeOffsKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<Record<string, unknown>>>(
        `/billing/write-offs/${id}`,
      );
      return mapWriteOff(response.data.data);
    },
    enabled: !!id,
  });
}

/**
 * Create a write-off against an invoice.
 */
export function useCreateWriteOff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateWriteOffPayload) => {
      const response = await apiClient.post<ApiResponse<Record<string, unknown>>>(
        '/billing/write-offs',
        data,
      );
      return mapWriteOff(response.data.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: writeOffsKeys.all });
      queryClient.invalidateQueries({ queryKey: ['billing', 'invoices'] });
      queryClient.invalidateQueries({ queryKey: ['billing', 'client-ledger'] });
    },
  });
}

/**
 * Void a write-off.
 */
export function useVoidWriteOff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: VoidWriteOffPayload }) => {
      const response = await apiClient.post<ApiResponse<Record<string, unknown>>>(
        `/billing/write-offs/${id}/void`,
        data,
      );
      return mapWriteOff(response.data.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: writeOffsKeys.all });
      queryClient.invalidateQueries({ queryKey: ['billing', 'invoices'] });
      queryClient.invalidateQueries({ queryKey: ['billing', 'client-ledger'] });
    },
  });
}
